import { BrandMark } from '../BrandMark';
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, FolderOpen, ClipboardCheck, Layers, FileText, Boxes, Link2, ScrollText, KeyRound, Settings, BarChart3, LogOut, ChevronDown, Search, Menu, X, UserCircle2, LifeBuoy, Eye, Palette, Check } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { cn } from '../../lib/utils';
import { NotificationBell } from './NotificationBell';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '../ui/dropdown-menu';
import { Input } from '../ui/input';

const NAV = [
  { section: 'Operate', items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/records', label: 'Records', icon: FolderOpen, menu: 'records' },
    { to: '/review', label: 'Review queue', icon: ClipboardCheck, badgeKey: 'review', menu: 'review' },
    { to: '/requests', label: 'Secure requests', icon: Link2, menu: 'requests' },
    { to: '/reports', label: 'Reports & SLA', icon: BarChart3, menu: 'reports' },
  ] },
  { section: 'Configure', items: [
    { to: '/config/classes', label: 'KYC classes', icon: Layers, menu: 'classes' },
    { to: '/config/fields', label: 'Field library', icon: Boxes, menu: 'fields' },
    { to: '/config/documents', label: 'Document library', icon: FileText, menu: 'documents' },
  ] },
  { section: 'Govern', items: [
    { to: '/audit', label: 'Audit log', icon: ScrollText, menu: 'audit' },
    { to: '/integrations', label: 'API clients', icon: KeyRound, menu: 'integrations' },
    { to: '/settings', label: 'Settings', icon: Settings, menu: 'settings' },
    { to: '/help', label: 'Help & guides', icon: LifeBuoy },
  ] },
];

export default function AppShell() {
  const app = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  if (app.loading && !app.records.length) return <div className="min-h-screen bg-paper-100 flex items-center justify-center text-ink-400"><div className="flex items-center gap-3 text-sm"><span className="h-4 w-4 rounded-full border-2 border-ink-300 border-t-ink-700 animate-spin" />Loading workspace…</div></div>;
  if (!app.currentUser) return <Navigate to="/login" replace />;
  const user = app.currentUser;
  const roleName = app.roleName(user.role);
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((it) => !it.menu || app.can(it.menu)) })).filter((g) => g.items.length);
  const reviewCount = app.allAssessments.filter((a) => ['submitted', 'in_review'].includes(a.status)).length;

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/records?q=${encodeURIComponent(q.trim())}`);
  };

  const Sidebar = (
    <aside className="flex h-full w-[248px] flex-col border-r border-line bg-paper-50">
      <div className="flex items-center px-5 h-16 border-b border-line"><BrandMark subtitle="KYC document management" /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {nav.map((group) => (
          <div key={group.section}>
            <div className="px-2 mb-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-400">{group.section}</div>
            <div className="space-y-0.5">
              {group.items.map((it) => (
                <NavLink key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={() => { setOpen(false); app.refresh(); }}
                  className={({ isActive }) => cn('group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors duration-150', isActive ? 'bg-white text-ink-800 shadow-card border border-line' : 'text-ink-500 hover:text-ink-800 hover:bg-white/70 border border-transparent')}>
                  <it.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{it.label}</span>
                  {it.badgeKey === 'review' && reviewCount > 0 && <span className="rounded-full bg-tint/15 text-ink-800 text-[11px] px-1.5 py-0.5 font-semibold tabular-nums">{reviewCount}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-line p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-white/80 transition-colors duration-150 text-left">
              <div className="h-8 w-8 rounded-full bg-ink-100 text-ink-700 flex items-center justify-center text-xs font-semibold">{user.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-ink-800 truncate">{user.name}</div>
                <div className="text-[11px] text-ink-400 truncate">{roleName}{app.access.classScope && <span data-testid="scope-chip" title={`Scoped to ${app.access.classScope.map((id) => app.classes.find((c) => c.id === id)?.code || id).join(', ')}`}> · scoped to {app.access.classScope.map((id) => app.classes.find((c) => c.id === id)?.code || id).join(', ')}</span>}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-ink-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs text-ink-400">{user.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/profile')}><UserCircle2 className="h-4 w-4 mr-2" />Profile & password</DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="theme-menu"><Palette className="h-4 w-4 mr-2" />Theme<span className="ml-auto text-[11px] text-ink-400 pl-3 truncate max-w-[90px]">{app.palettes.find((p) => p.id === app.activeThemeId)?.name}</span></DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-60">
                <DropdownMenuItem data-testid="theme-default" onClick={() => app.setTheme(null)}><span className={cn('h-3.5 w-3.5 mr-2 rounded-full border border-line', !app.themeId ? 'bg-ink-700' : 'bg-transparent')} />Workspace default<span className="ml-auto text-[11px] text-ink-400">{app.palettes.find((p) => p.id === app.defaultThemeId)?.name}</span></DropdownMenuItem>
                <DropdownMenuSeparator />
                {app.palettes.map((p) => <DropdownMenuItem key={p.id} data-testid={`theme-${p.id}`} onClick={() => app.setTheme(p.id)}><span className="h-3.5 w-3.5 mr-2 rounded-full border border-line shrink-0" style={{ background: `linear-gradient(135deg, ${p.paper} 50%, ${p.ink} 50%)` }} /><span className="truncate">{p.name}</span>{app.themeId === p.id && <Check className="h-3.5 w-3.5 ml-auto text-ink-500" />}</DropdownMenuItem>)}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem data-testid="logout-button" onClick={async () => { await app.logout(); navigate('/login'); }}><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-paper-100 text-ink-700 flex">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">{Sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-ink-800/30" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 shadow-lift">{Sidebar}</div>
          <button className="absolute top-4 left-[260px] h-9 w-9 rounded-lg bg-white border border-line flex items-center justify-center" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex-1 lg:pl-[248px] flex flex-col min-w-0">
        {app.preview && (
          <div data-testid="preview-banner" className="sticky top-0 z-30 bg-ink-800 text-paper-50 px-4 md:px-8 py-2 flex items-center gap-3 text-sm">
            <Eye className="h-4 w-4 shrink-0 text-tint" />
            <div className="min-w-0 flex-1 truncate">Previewing as <span className="font-semibold">{app.preview.name}</span> <span className="text-ink-300">(unsaved draft) · UI preview only — the API still uses your own permissions</span></div>
            <button data-testid="exit-preview" onClick={() => { app.exitPreview(); navigate('/settings/roles'); }} className="rounded-full bg-paper-50 text-ink-800 text-xs font-semibold px-3 py-1 hover:bg-white transition-colors">Exit preview</button>
          </div>
        )}
        <header className={cn('sticky z-20 h-16 border-b border-line bg-paper-100/80 backdrop-blur flex items-center gap-3 px-4 md:px-8', app.preview ? 'top-9' : 'top-0')}>
          <button className="lg:hidden h-9 w-9 rounded-lg border border-line bg-white flex items-center justify-center" onClick={() => setOpen(true)}><Menu className="h-4 w-4" /></button>
          <form onSubmit={onSearch} className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records by name, reference, email or registration no." className="pl-9 h-9 bg-white border-line" />
          </form>
          <div className="ml-auto flex items-center gap-2">
            {user.mustChangePassword && <button onClick={() => navigate('/settings/profile')} className="hidden md:inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">Temporary password — update now</button>}
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 px-4 md:px-8 py-8 max-w-[1400px] w-full mx-auto page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
